import { buildOpportunityCustomFields, pickPortfolioImage } from './buildOpportunityCustomFields';
import { buildPortfolioLeadEntry, buildSingleLeadEntry } from './buildPortfolioLead';
import { buildMessageVars } from './messageTokens';

// One config table replaces the three near-identical CRM provider blocks
// (GHL / FUB / Salesforce) that previously lived inline in LeadBuyerMatches.js
// (~600 lines of triplicated JSX + cta handlers).
//
// Each provider entry describes:
//   - crmLabel: user-facing name
//   - actionVerb: noun for the create action ('Opportunity' | 'Deal')
//   - selectors: which selector inputs to render (['pipeline','stage'] | ['stage'])
//   - createEnv: env var key for the create endpoint
//   - fetchPipelinesEnv: env var key for the selector-fetch endpoint (null for SF: stages only)
//   - buildCreatePayload({ context, selection, tags, portfolioCtx }) → payload object
//   - buildSelfPurchasePayload({ context, selection, tags }) → payload object for
//     the operator buying the deal THEMSELVES (no buyer). Same endpoint, but the
//     backend takes its `selfPurchase` branch: it attaches the opportunity to the
//     SELLER contact and skips every buyer-only extra (message, subject, buy-box
//     fit, tracked deal link), so we omit those fields here.

// A large send can exceed the gateway/function timeout (504), fail with another
// 5xx, or drop as a network error (coerced to 406 by cloudFunctionV2) — yet the
// backend keeps running and usually finishes creating the records. Treat those as
// "still working" rather than a hard failure, or the user retries and double-books:
// neither the buyer nor the self-purchase create path dedupes.
export const isLikelyTimeout = (status) =>
    status === 504 || status === 406 || (typeof status === 'number' && status >= 500);
// Copy kept verbatim from the buyer send path so moving it here changes nothing
// a user sees; it reads correctly for a self-purchase save too.
export const TIMEOUT_SUMMARY = 'Still sending — check your CRM';
export const TIMEOUT_MESSAGE = 'This is taking longer than usual — the send may still be finishing in the background. '
    + 'Check your CRM in a minute, then refresh this page before retrying so you don\'t create duplicates.';

function buildOpportunityName(lead, propertyId) {
    const addr = lead?.address || {};
    const full = [
        addr.streetAddress,
        addr.city,
        `${addr.state || ''} ${addr.zipcode || ''}`.trim(),
    ].filter(Boolean).join(', ');
    return full || propertyId;
}

function buildOpportunityValue(calculatorInputs) {
    const wf = Number(calculatorInputs?.wholesaleFee);
    if (wf > 0) return wf;
    return Number(calculatorInputs?.purchasePrice) || 0;
}

// A self-purchase has no wholesale fee to earn — the opportunity is worth what
// we'd pay for the house, so the fee preference above must not apply.
function buildSelfPurchaseValue(calculatorInputs) {
    return Number(calculatorInputs?.purchasePrice) || 0;
}

// The seller is the counterparty on a self-purchase, so their contact is what the
// opportunity hangs off (GHL won't create one without a contact).
//
// Same fallback chain LeadDetailV2 uses to DISPLAY the sender (LeadDetailV2.js:2009),
// so the CRM contact is whoever the deal page says sent the deal. currentSender is
// stamped at ingestion but is all-nulls when the source had no sender
// (propertyIngestionV2/batchedFirestore.js:673), hence the layering.
//
// `name` falls back to the opportunity name (the address) only so the request isn't
// rejected outright — callers should warn the operator first, since a nameless
// sender books a CRM contact named after the property. See sellerContactIsUsable.
export function resolveSellerContact(lead) {
    return lead?.currentSender
        || lead?.originalSender
        || lead?.entries?.[0]?.sender
        || {};
}

// GHL/FUB can only match or create a real person off an email or phone; a bare name
// yields a junk contact. Lets the UI warn before the operator sends.
export function sellerContactIsUsable(lead) {
    const sender = resolveSellerContact(lead);
    return !!(sender.email || sender.phone);
}

function buildSellerContact(context, fallbackName) {
    const sender = resolveSellerContact(context.lead);
    return {
        name: sender.name || fallbackName,
        email: sender.email || null,
        phone: sender.phone || null,
    };
}

// Convert a datetime-local string ("YYYY-MM-DDTHH:mm") into GHL's accepted
// custom-field datetime format ("MM-DD-YYYY hh:mm AM/PM", e.g. "12-21-2021 08:30 AM").
//
// The wall-clock time the operator picked is preserved verbatim — NO timezone
// conversion. GHL's "Set Event Start Date" workflow action interprets the
// string in the sub-account's own timezone, so converting to UTC (or the
// property's timezone) here would shift when the automation actually fires.
// Returns null for empty/malformed input so the caller can omit the field.
export function formatSendAtForGhl(localValue) {
    if (!localValue || typeof localValue !== 'string') return null;
    const m = localValue.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (!m) return null;
    const [, year, month, day, hourStr, minute] = m;
    let hour = parseInt(hourStr, 10);
    const meridiem = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    const hh = String(hour).padStart(2, '0');
    return `${month}-${day}-${year} ${hh}:${minute} ${meridiem}`;
}

// Single shared resolver — every provider builds the same opportunity shape (name,
// value, customFields) when not in a portfolio; portfolio mode swaps in the
// combined-portfolio name + price. `provider` controls which custom-field keys
// are included (GHL gets address fields; FUB/SF do not — historic backend shape).
function resolveOpportunityBasics({ context, provider, selfPurchase = false }) {
    const { lead, calculatorInputs, propertyId, isPortfolio, portfolio, portfolioLeads, coLivingReturns, displayImageUrl } = context;

    if (isPortfolio && portfolio) {
        const portfolioPrice = portfolio.combinedAskingPrice
            || (portfolioLeads || []).reduce((sum, pl) => sum + (Number(pl.currentAskingPrice) || 0), 0);
        const firstLead = (portfolioLeads || [])[0];
        const portfolioImage = pickPortfolioImage(portfolioLeads);
        return {
            name: `${portfolio.name || 'Portfolio'} (${portfolio.leadCount || portfolioLeads.length} properties)`,
            value: portfolioPrice || 0,
            customFieldValues: {
                Bedrooms: null,
                Bathrooms: null,
                'Square Footage': null,
                'Year Built': null,
                Price: portfolioPrice || null,
                Image: portfolioImage,
                clozers_opportunity_id: lead.portfolioId,
                Occupancy: null,
                Condition: null,
                ARV: null,
                'Repair Cost': null,
                'Property Count': portfolio.leadCount || portfolioLeads.length,
                'Zip Code': firstLead?.address?.zipcode || null,
            },
        };
    }

    return {
        name: buildOpportunityName(lead, propertyId),
        value: selfPurchase
            ? buildSelfPurchaseValue(calculatorInputs)
            : buildOpportunityValue(calculatorInputs),
        // displayImageUrl (the sending page's gallery hero) wins over the built-in
        // picker so the CRM "Image" matches what the operator sees. See
        // buildOpportunityCustomFields.
        customFieldValues: buildOpportunityCustomFields(lead, calculatorInputs, propertyId, provider, coLivingReturns, displayImageUrl),
    };
}

function buildPortfolioLeadsArray({ context, customFieldValues }) {
    const { lead, isPortfolio, portfolioLeads } = context;
    if (isPortfolio && Array.isArray(portfolioLeads) && portfolioLeads.length > 0) {
        return portfolioLeads.map(buildPortfolioLeadEntry);
    }
    return [buildSingleLeadEntry(lead, customFieldValues)];
}

export const CRM_PROVIDERS = {
    ghl: {
        crmLabel: 'GoHighLevel',
        actionVerb: 'Opportunity',
        selectors: ['pipeline', 'stage'],
        fetchPipelinesEnv: 'REACT_APP_GET_GHL_OPPORTUNITIES',
        createEnv: 'REACT_APP_CREATE_GHL_OPPORTUNITY',
        buildCreatePayload: ({ context, selection, tags }) => {
            const basics = resolveOpportunityBasics({ context, provider: 'ghl' });
            // Operator-scheduled automation time → clozers_send_at custom field.
            // Backend resolves this key against GHL's opportunity.clozers_send_at
            // fieldKey (createGHLOpportunity fuzzy-match), so no mapping needed.
            const sendAt = formatSendAtForGhl(context.sendAt);
            const customFieldValues = sendAt
                ? { ...basics.customFieldValues, clozers_send_at: sendAt }
                : basics.customFieldValues;
            const portfolioLeadsArr = buildPortfolioLeadsArray({ context, customFieldValues });
            // Per-deal custom buyer message → backend ensures opportunity.clozers_message
            // (LARGE_TEXT), personalizes it per buyer, and stamps it on each buyer's
            // opportunity. Sent as a top-level field (not customFieldValues) so it
            // bypasses the mapping/fuzzy-match path. messageVars carries the deal-level
            // token values ({{street_address}}, {{price}}, {{featured_image}}); the
            // backend fills the per-buyer tokens ({{first_name}}, {{last_name}},
            // {{deal_link}}) itself.
            const dealMessage = (context.dealMessage || '').trim();
            // Per-deal email SUBJECT → backend ensures opportunity.clozers_subject
            // (TEXT), personalizes it per buyer with the SAME messageVars, and stamps
            // it on each buyer's opportunity so the client's send workflow can use
            // {{ opportunity.clozers_subject }} as the email subject line.
            const dealSubject = (context.dealSubject || '').trim();
            // origin is needed to build the deal-link base; companySlug rides on the context.
            const origin = (typeof window !== 'undefined' && window.location ? window.location.origin : '');
            const messageVars = buildMessageVars({
                ...context,
                origin,
            });
            // Per-buyer buy-box fit → opportunity.clozers_buybox. Project each SELECTED
            // buyer's persisted match ({ breakdown }) so the backend can
            // build a per-buyer "why we sent you this" block. Single-lead only — a
            // package's per-member fit doesn't collapse to one value, so we omit it for
            // portfolios (backend simply stamps nothing). buyBoxEditLinkBase reuses the
            // existing no-login buyer editor at /deals/{slug}/feed/{buyerId}; the backend
            // appends /{buyerId}?tab=edit per buyer.
            const buyBoxFitByBuyerId = {};
            if (!context.isPortfolio && context.companySlug) {
                const selected = context.selectedBuyerIds instanceof Set
                    ? context.selectedBuyerIds
                    : new Set(context.selectedBuyerIds || []);
                for (const m of (context.lead?.buyerMatches?.matches || [])) {
                    if (m?.buyerId && m.breakdown && selected.has(m.buyerId)) {
                        buyBoxFitByBuyerId[m.buyerId] = { breakdown: m.breakdown };
                    }
                }
            }
            const hasBuyBoxFit = Object.keys(buyBoxFitByBuyerId).length > 0;
            const buyBoxEditLinkBase = hasBuyBoxFit && origin && context.companySlug
                ? `${origin}/deals/${context.companySlug}/feed`
                : '';
            return {
                companyId: context.companyId,
                pipelineId: selection.pipelineId,
                stageId: selection.stageId,
                buyerIds: Array.from(context.selectedBuyerIds),
                name: basics.name,
                status: 'open',
                monetaryValue: basics.value,
                customFieldValues,
                tagNames: tags,
                portfolioLeads: portfolioLeadsArr,
                // messageVars carries the deal-level token values (shared by the body +
                // subject substitution) AND the deal-link base — the backend needs the
                // latter to stamp each buyer's own short link onto the clozers_deal_link
                // field even when there's no message/subject, so always send it.
                messageVars,
                ...(dealMessage ? { dealMessage } : {}),
                ...(dealSubject ? { dealSubject } : {}),
                ...(hasBuyBoxFit ? { buyBoxFitByBuyerId, buyBoxEditLinkBase } : {}),
            };
        },
        buildSelfPurchasePayload: ({ context, selection, tags }) => {
            const basics = resolveOpportunityBasics({ context, provider: 'ghl', selfPurchase: true });
            return {
                companyId: context.companyId,
                selfPurchase: true,
                pipelineId: selection.pipelineId,
                stageId: selection.stageId,
                name: basics.name,
                status: 'open',
                monetaryValue: basics.value,
                customFieldValues: basics.customFieldValues,
                contact: buildSellerContact(context, basics.name),
                tagNames: tags,
                portfolioLeads: buildPortfolioLeadsArray({ context, customFieldValues: basics.customFieldValues }),
            };
        },
    },

    fub: {
        crmLabel: 'Follow Up Boss',
        actionVerb: 'Deal',
        selectors: ['pipeline', 'stage'],
        fetchPipelinesEnv: 'REACT_APP_GET_FUB_DEAL_STAGES',
        createEnv: 'REACT_APP_CREATE_FUB_DEAL',
        buildCreatePayload: ({ context, selection, tags }) => {
            const basics = resolveOpportunityBasics({ context, provider: 'fub' });
            return {
                companyId: context.companyId,
                buyerIds: Array.from(context.selectedBuyerIds),
                pipelineId: selection.pipelineId,
                stageId: selection.stageId,
                dealPrice: basics.value,
                propertyAddress: context.isPortfolio ? null : basics.name,
                dealName: basics.name,
                customFieldValues: basics.customFieldValues,
                tagNames: tags,
            };
        },
        buildSelfPurchasePayload: ({ context, selection, tags }) => {
            const basics = resolveOpportunityBasics({ context, provider: 'fub', selfPurchase: true });
            return {
                companyId: context.companyId,
                selfPurchase: true,
                pipelineId: selection.pipelineId,
                stageId: selection.stageId,
                dealPrice: basics.value,
                propertyAddress: context.isPortfolio ? null : basics.name,
                dealName: basics.name,
                customFieldValues: basics.customFieldValues,
                contact: buildSellerContact(context, basics.name),
                tagNames: tags,
            };
        },
    },

    salesforce: {
        crmLabel: 'Salesforce',
        actionVerb: 'Opportunity',
        selectors: ['stage'],
        fetchPipelinesEnv: 'REACT_APP_GET_SF_OPPORTUNITY_STAGES',
        createEnv: 'REACT_APP_CREATE_SF_OPPORTUNITY',
        buildCreatePayload: ({ context, selection, tags }) => {
            const basics = resolveOpportunityBasics({ context, provider: 'salesforce' });
            return {
                companyId: context.companyId,
                buyerIds: Array.from(context.selectedBuyerIds),
                stageName: selection.stageId, // SF passes the stage *name* in this field
                name: basics.name,
                amount: basics.value,
                customFieldValues: basics.customFieldValues,
                propertyId: context.propertyId,
                tagNames: tags,
            };
        },
        buildSelfPurchasePayload: ({ context, selection, tags }) => {
            const basics = resolveOpportunityBasics({ context, provider: 'salesforce', selfPurchase: true });
            return {
                companyId: context.companyId,
                selfPurchase: true,
                stageName: selection.stageId, // SF passes the stage *name* in this field
                name: basics.name,
                amount: basics.value,
                customFieldValues: basics.customFieldValues,
                contact: buildSellerContact(context, basics.name),
                propertyId: context.propertyId,
                tagNames: tags,
            };
        },
    },
};

// Shared result-message formatter — every provider returns { created, skipped, failed,
// results: [{ buyerName, reason, tagsApplied, tagsFailed }] }, so the parsing is identical.

// Counts only — short enough for a button label.
export function formatCreateSummary(data) {
    const parts = [];
    if (data.created > 0) parts.push(`${data.created} created`);
    if (data.skipped > 0) parts.push(`${data.skipped} skipped`);
    if (data.failed > 0) parts.push(`${data.failed} failed`);
    const totalTagsApplied = (data.results || []).reduce((s, r) => s + (r.tagsApplied?.length || 0), 0);
    const totalTagsFailed = (data.results || []).reduce((s, r) => s + (r.tagsFailed?.length || 0), 0);
    if (totalTagsApplied > 0) parts.push(`${totalTagsApplied} tags applied`);
    if (totalTagsFailed > 0) parts.push(`${totalTagsFailed} tags failed`);
    return parts.join(', ');
}

// The per-buyer reasons anything failed or was skipped, tagged with the buyer
// name when the backend supplied one ("Mark Couch: their email looks invalid").
// De-duped so a bulk send doesn't repeat the same reason for every buyer.
export function formatCreateIssues(data) {
    const issues = (data.results || [])
        .filter((r) => (r.status === 'failed' || r.status === 'skipped') && r.reason)
        .map((r) => (r.buyerName ? `${r.buyerName}: ${r.reason}` : r.reason));
    return Array.from(new Set(issues));
}

// Counts plus the reasons — what we show in the result line / success card so
// the user knows exactly which buyer to fix and why, not just "1 failed".
export function formatCreateResult(data) {
    const summary = formatCreateSummary(data);
    const issues = formatCreateIssues(data);
    return issues.length > 0 ? `${summary} — ${issues.join('; ')}` : summary;
}
